import { useFormikContext } from 'formik';
import Skeleton from 'react-loading-skeleton';

import type { PriceMessage } from '../../../../types/PricesMessage';
import { Button } from '../../../cdl/Button/Button';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { Box } from '../../../common/ui/Box';
import { FormikComparePortSelect } from '../FormikComparePortSelect';
import { ComparePriceWrapper } from '../overlay/ComparePriceWrapper';
import { PortPriceRow } from '../PortPriceRow';
import { RemoveCompareRowButton } from '../RemoveCompareRowButton';
import type { PriceCompareFormValues } from '../types/PriceCompareFormValues';

import { AddKeyPort } from './AddKeyPort';
import { PriceBox } from './PriceBox';

interface EnquiryButtonProps {
    isFetching: boolean;
    supplierData?: PriceMessage[];
    onButtonClick: () => void;
}

const EnquiryButton = ({ isFetching, supplierData, onButtonClick }: EnquiryButtonProps) => {
    if (isFetching) {
        return (
            <Box width="155px">
                <Skeleton height="40px" />
            </Box>
        );
    }

    if (supplierData) {
        return (
            <Button type="button" LeadingIcon={IconPlus} emphasis="high" onClick={onButtonClick}>
                {translate('compare.createEnquiry')}
            </Button>
        );
    }

    return <Box width="155px" />;
};

interface PortPriceInformationProps {
    index: number;
    onEnquiryButtonClick: (portId?: string) => void;
    onRemove: () => void;
    isFetching: boolean;
    supplierData?: PriceMessage[];
    portId?: string;
    showRemoveIcon: boolean;
}

export const PortPriceInformation = ({
    index,
    onEnquiryButtonClick,
    onRemove,
    isFetching,
    supplierData,
    portId,
    showRemoveIcon,
}: PortPriceInformationProps) => {
    const role = useRole();
    const { values } = useFormikContext<PriceCompareFormValues>();

    return (
        <PortPriceRow>
            {showRemoveIcon ? <RemoveCompareRowButton onClick={onRemove} /> : null}
            <Box position="relative" width="180px" style={{ flexShrink: 0 }}>
                <FormikComparePortSelect name={`portInformation[${index}].portId`} />

                {portId && role.hasWriteRights(values.customerId) && role.isCustomer() ? (
                    <Box position="absolute" marginTop={6}>
                        <AddKeyPort portId={portId} />
                    </Box>
                ) : null}
            </Box>

            <ComparePriceWrapper isFetching={isFetching} supplierData={supplierData}>
                {supplierData?.map((priceInfo) => {
                    const key = `${priceInfo.supplierId}:${priceInfo.contractId ?? ''}`;
                    return (
                        <PriceBox
                            price={priceInfo}
                            contractName={priceInfo.contract?.name}
                            isDefaultContract={priceInfo.contractId === values.vessel?.defaultContractId}
                            key={key}
                        />
                    );
                }) ?? []}
            </ComparePriceWrapper>

            {role.isCustomer() && role.hasWriteRights(values.customerId) ? (
                <Box style={{ flexShrink: 0 }}>
                    <EnquiryButton
                        isFetching={isFetching}
                        supplierData={supplierData}
                        onButtonClick={() => onEnquiryButtonClick(portId)}
                    />
                </Box>
            ) : null}
        </PortPriceRow>
    );
};
